import { downgradeComponent, downgradeInjectable } from "@angular/upgrade/static";
import { VehicleVideosPageComponent } from "@app/main/components/videos/vehicleVideosPage/vehicleVideosPage.component";
import { CameraSignalStrengthComponent } from "@app/main/modules/cameras/_components/camera-signal-strength/camera-signal-strength.component";
import { DownloadQualitySelector } from "@app/main/modules/cameras/_components/download-quality-selector/download-quality-selector.component";
import { VideoDownloadListComponent } from "@app/main/modules/cameras/_components/video-download-list/video-download-list.component";
import { VideoEventViewComponent } from "@app/main/modules/cameras/_components/videoEventView/videoEventView.component";
import { RouteConfigHelperProvider } from "@app/services/routeConfigHelper.provider";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AuthorizationService } from "@services/authorization.service";
import { StateProvider } from "@uirouter/angularjs";
import * as angular from "angular";
import { VideoAddressComponent } from "../../modules/cameras/_components/video-address/video-address.component";
import { VideoEntryComponent } from "../../modules/cameras/_components/video-entry/video-entry.component";
import { VideoEventListComponent } from "../../modules/cameras/_components/video-event-list/video-event-list.component";
import { VideoListComponent } from "../../modules/cameras/_components/video-list/video-list.component";
import { CameraModule } from "../../modules/cameras/camera.module.ajs";
import { CompositeVideoPlayerComponent } from "./compositeVideoPlayer/compositeVideoPlayer.component";
import { DriverOverviewPageComponent } from "./driverOverviewPage/driverOverviewPage.component";
import { DriverVideoCardComponent } from "./driverVideoCard/driverVideoCard.component";
import { DriverVideosPageComponent } from "./driverVideosPage/driverVideosPage.component";
import { VideoEvidenceComponent } from "./evidence/videoEvidence";
import { LiveStreamVideoComponent } from "./liveStreamVideo/liveStreamVideo.component";
import { NotesComponent } from "./notes/notes.component";
import { RequestVideoFormComponent } from "./requestVideo/request-video-form/request-video-form.component";
import { RequestVideoComponent } from "./requestVideo/requestVideo.component";
import { RequestVideoService } from "./requestVideo/requestVideo.service";
import { RequestVideoActivityChartComponent } from "./requestVideo/requestVideoActivityChart/request-video-activity-chart.component";
import { RequestVideoMapComponent } from "./requestVideo/requestVideoMap/requestVideoMap.component";
import { RequestVideoQueryStatusComponent } from "./requestVideo/requestVideoQueryStatus/requestVideoQueryStatus.component";
import { CameraFootageTimelineService } from "./services/camera-footage-timeline.service";
import { CameraFootageService } from "./services/camera-footage.service";
import { CameraOnlineHistoryService } from "./services/camera-online-history.service";
import { CameraStatusService } from "./services/camera-status.service";
import { CameraService } from "./services/camera.service";
import { VideoEntryService } from "./services/video-entry.service";
import { VideoEventCodeService } from "./services/video-event-code.service";
import { VideoReviewStatusService } from "./services/video-review-status.service";
import { VideoReviewService } from "./services/video-review.service";
import { VehicleVideoCardComponent } from "./vehicleVideoCard/vehicleVideoCard.component";
import { VideoChannelSelectorComponent } from "./video-channel-selector/video-channel-selector.component";
import { VideoCoachingPageComponent } from "./videoCoachingPage/videoCoachingPage.component";
import { JourneyOverviewCardComponent } from "./videoJourneySummary/journeyOverviewCard/journeyOverviewCard.component";
import { JourneySummaryCardComponent } from "./videoJourneySummary/journeySummaryCard/journeySummaryCard.component";
import { VideoJourneySummaryComponent } from "./videoJourneySummary/videoJourneySummary.component";
import { VideoPageController } from "./videoPageController.controller";
import "./videos.scss";

export class Config {

    static $inject = ["$stateProvider", "routeConfigHelperProvider"];

    constructor($stateProvider: StateProvider, private routeConfigHelperProvider: RouteConfigHelperProvider) {
        $stateProvider.state(CameraModule.Videos, {
            url: "/videos",
            views: { "content@app": { component: "videoList" } },
            data: { translation: "Common.Videos", subtitleTranslationKey: "Videos.VideosSubtitle" }
        });

        $stateProvider.state(CameraModule.Events, {
            url: "/events",
            views: { "content@app": { component: "videoEventList" } },
            data: { translation: "Videos.Events", subtitleTranslationKey: "Videos.EventsSubtitle" }
        });

        this.routeConfigHelperProvider.createDetailStateWithBoundParams({
            stateName: CameraModule.EventView,
            translation: 'VideoEventView.ViewEvent',
            bindingName: "eventId",
            component: "videoEventView",
            breadcrumbNameResolverKey: "eventNameKey"
        });

        $stateProvider.state(CameraModule.History, {
            url: "/history",
            views: { "content@app": { component: "videoJourneySummary" } },
            data: { translation: "Common.History" }
        });

        $stateProvider.state(CameraModule.Coaching, {
            url: "/coaching",
            views: { "content@app": { component: "videoCoachingPage" } },
            data: { translation: "Videos.Coaching", subtitleTranslationKey: "Videos.CoachingSubtitle" }
        });

        $stateProvider.state(CameraModule.Vehicles, {
            url: "/vehicles",
            views: { "content@app": { component: "vehicleVideosPage" } },
            data: { translation: "Common.Vehicles", subtitleTranslationKey: "Videos.VehiclesSubtitle" }
        });

        this.routeConfigHelperProvider.createDetailStateWithBoundParams({
            stateName: `${CameraModule.VehicleView}`,
            bindingName: "vehicleId",
            component: "vehicleVideoCard",
            queryParamsNames: ["tab", "requestVideoTime"],
            breadcrumbNameResolverKey: "vehicleVideoNameKey"
        });

        //$stateProvider.state(CameraModule.VehiclesNew, { url: "/vehiclesnew", views: { "content@app": { component: "vehiclesPage" } } });
        $stateProvider.state(CameraModule.Drivers, {
            url: "/drivers",
            views: { "content@app": { component: "driverVideosPage" } },
            data: { translation: "Common.Drivers", subtitleTranslationKey: "Videos.DriverSubtitle" }
        });

        $stateProvider.state(CameraModule.Downloads, {
            url: "/downloads",
            views: { "content@app": { component: "videoDownloadList" } },
            data: { translation: "Videos.Downloads", subtitleTranslationKey: "Videos.DownloadsSubtitle" }
        });

        $stateProvider.state(CameraModule.ParentRoot, {
            abstract: true,
            url: "/cameras",
            redirectTo: CameraModule.Default,
            //views: {
            //    "module-navigation": {
            //        component: "cameraModuleNavigation"
            //    }
            //},
            data: { translation: "Videos.Cameras" },
            resolve: {
                authorize: ["authorizationService", (authorization: AuthorizationService) => authorization.authorizeForUser([], [AccountFeatures.Camera])]
            }
        })
    }
}

angular
    .module("app.components.videos", ["ui.router"])
    .config(Config)
    .controller("videoPageController", VideoPageController)

    .service("requestVideoService", RequestVideoService)
    .service("videoEventCodeService", downgradeInjectable(VideoEventCodeService))
    .service("videoEntryService", downgradeInjectable(VideoEntryService))
    .service("cameraOnlineHistoryService", downgradeInjectable(CameraOnlineHistoryService))
    .service("cameraService", downgradeInjectable(CameraService))
    .service("cameraFootageService", downgradeInjectable(CameraFootageService))
    .service("cameraFootageTimelineService", downgradeInjectable(CameraFootageTimelineService))
    .service("cameraStatusService", downgradeInjectable(CameraStatusService))
    .service("videoReviewService", downgradeInjectable(VideoReviewService))
    .service("videoReviewStatusService", downgradeInjectable(VideoReviewStatusService))

    .directive("videoDownloadList", downgradeComponent({ component: VideoDownloadListComponent }))
    .directive("videoEventList", downgradeComponent({ component: VideoEventListComponent }))
    .directive("videoEntry", downgradeComponent({ component: VideoEntryComponent }))
    .directive("videoAddress", downgradeComponent({ component: VideoAddressComponent }))
    .directive("videoList", downgradeComponent({ component: VideoListComponent }))
    .directive("cameraSignalStrength", downgradeComponent({ component: CameraSignalStrengthComponent }))
    .directive("requestVideoForm", downgradeComponent({ component: RequestVideoFormComponent }))
    .directive("videoChannelSelector", downgradeComponent({ component: VideoChannelSelectorComponent }))
    .directive("downloadQualitySelector", downgradeComponent({ component: DownloadQualitySelector }))

    .component("videoNotes", new NotesComponent())
    .component("videoEvidence", new VideoEvidenceComponent())
    .component('videoJourneySummary', new VideoJourneySummaryComponent())
    .component('journeySummaryCard', new JourneySummaryCardComponent())
    .component('journeyOverviewCard', new JourneyOverviewCardComponent())
    .component('driverOverviewPage', new DriverOverviewPageComponent())

    .component("liveStreamVideo", new LiveStreamVideoComponent())

    .component("requestVideo", new RequestVideoComponent())
    .component("requestVideoMap", new RequestVideoMapComponent)
    .directive("requestVideoActivityChart", downgradeComponent({ component: RequestVideoActivityChartComponent }))
    .component("requestVideoQueryStatus", new RequestVideoQueryStatusComponent())

    .component("compositeVideoPlayer", new CompositeVideoPlayerComponent())

    .component("videoCoachingPage", new VideoCoachingPageComponent())
    .component("vehicleVideoCard", new VehicleVideoCardComponent())

    .component("videoEventView", new VideoEventViewComponent())
    .component("driverVideosPage", new DriverVideosPageComponent())
    .component("driverVideoCard", new DriverVideoCardComponent())
    .component("vehicleVideosPage", new VehicleVideosPageComponent());
